import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CgArrowsExpandLeft } from 'react-icons/cg';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BrandStoreForMap, StateStoreCount, fetchNeighborhoodStoresForViewport } from '../../services/mapPageService';
import { LayerData } from '../../components/LayerCard/LayerCard';
import { isTauri } from '../../utils/tauri';
import { useLogoCaching } from '../storesMap/useLogoCaching';
import { useViewportNeighborhoods } from './useViewportNeighborhoods';
import { createSimpleMarker, createDetailedMarker, getMarkerScaledSize, getMarkerAnchor, createStateClusterMarker, createRegionClusterIcon, getAnonymousStoreIcon, getZoomScale } from '../../utils/markerUtils';
import { buildInfoWindowContent } from '../../utils/infoWindowContent';
import { loadGoogleMaps } from '../../utils/googleMaps';
import { useCrimeHeatmap } from './useCrimeHeatmap';
import { calculateStoreRecommendations } from '../../utils/recommendationLogic';
import { getStateCoords } from './useRegionView';
import StreetViewPanel from './StreetViewPanel';
import './OpportunityMap.css';

export interface RecommendationConfigs {
  purchekConditions?: any;
  cartManagerConditions?: any;
  containmentConditions?: any;
  subscriptionConditions?: any;
  pairedWheelConditions?: any;
  purchekReadinessName?: string;
  cartManagerReadinessName?: string;
  containmentReadinessName?: string;
  subscriptionReadinessName?: string;
  pairedWheelReadinessName?: string;
}

interface OpportunityMapProps {
  hasBrandLayers?: boolean;
  layerStores?: Record<string, BrandStoreForMap[]>;
  layersWithStores?: LayerData[];
  recommendationConfigs?: RecommendationConfigs;
  showHeatmap?: boolean;
  regionViewEnabled?: boolean;
  stateAggregates?: StateStoreCount[];
  regionViewStores?: BrandStoreForMap[];
  regionZoomTier?: 'state' | 'cluster' | 'detail';
  onMapReady?: (map: google.maps.Map) => void;
  showNeighbourhood?: boolean;
  neighbourhoodRetailers?: string[];
  neighbourhoodChannels?: string[];
  neighbourhoodStatus?: 'All' | 'Customers' | 'Non-Customers';
  neighbourhoodSolutions?: string[];
  neighbourhoodWhitespace?: string[];
  neighbourhoodAnonymous?: boolean;
  hideClustering?: boolean;
  markerSize?: number;
  regionLoading?: boolean;
}

export interface OpportunityMapHandle {
  focusStore: (storeId: string, lat: number, lng: number) => void;
  getViewportNeighborhoods: () => Record<string, BrandStoreForMap[]>;
}

interface MarkerInfo {
  marker: google.maps.Marker;
  store: BrandStoreForMap;
  layer: LayerData;
  logoDataUrl: string;
  storeLabels: string[];
  isPrimaryStore: boolean;
  storeId: string;
}

const OpportunityMap = forwardRef<OpportunityMapHandle, OpportunityMapProps>(({
  hasBrandLayers = false,
  layerStores = {},
  layersWithStores = [],
  recommendationConfigs = {},
  showHeatmap = false,
  regionViewEnabled = false,
  stateAggregates = [],
  regionViewStores = [],
  regionZoomTier = 'state',
  onMapReady,
  showNeighbourhood = false,
  neighbourhoodRetailers = [],
  neighbourhoodChannels = [],
  neighbourhoodStatus = 'All',
  neighbourhoodSolutions = [],
  neighbourhoodWhitespace = [],
  neighbourhoodAnonymous = false,
  hideClustering = false,
  markerSize = 100,
  regionLoading = false,
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [mapZoom, setMapZoom] = useState(4);
  const markersRef = useRef<MarkerInfo[]>([]);
  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const zoomModeRef = useRef<boolean>(false);
  const { fetchBrandLogo } = useLogoCaching();
  const [streetViewStore, setStreetViewStore] = useState<{ lat: number; lng: number; name?: string } | null>(null);

  const updateDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const stateMarkersRef = useRef<google.maps.Marker[]>([]);
  const regionClustererRef = useRef<MarkerClusterer | null>(null);
  const regionMarkerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const regionMarkerCustomerMapRef = useRef<WeakMap<google.maps.Marker, boolean>>(new WeakMap());
  const markerBaseDimsRef = useRef<WeakMap<google.maps.Marker, { w: number; h: number; ax: number; ay: number }>>(new WeakMap());
  const regionViewCancelledRef = useRef(false);
  const prevRegionZoomTierRef = useRef<string>('');
  const prevSkipClusteringRef = useRef<boolean>(false);
  const hideClusteringRef = useRef(hideClustering);
  hideClusteringRef.current = hideClustering;
  const markerSizeRef = useRef(markerSize);
  markerSizeRef.current = markerSize;

  const pulseOverlaysRef = useRef<google.maps.OverlayView[]>([]);
  const [markerUpdateCount, setMarkerUpdateCount] = useState(0);

  // Neighbourhood stores for region view
  const neighbourhoodMarkerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const neighbourhoodBoundsKeyRef = useRef('');
  const regionPulseOverlaysRef = useRef<google.maps.OverlayView[]>([]);
  const infoWindowJustOpenedRef = useRef(false);

  const { viewportNeighborhoods } = useViewportNeighborhoods(
    map,
    layersWithStores,
    layerStores
  );

  useCrimeHeatmap(map, showHeatmap);

  const smoothZoomTo = useCallback((mapInstance: google.maps.Map, targetZoom: number, callback?: () => void) => {
    const currentZoomLevel = mapInstance.getZoom() || 4;
    if (currentZoomLevel === targetZoom) {
      callback?.();
      return;
    }

    const step = currentZoomLevel < targetZoom ? 1 : -1;
    const listener = google.maps.event.addListener(mapInstance, 'zoom_changed', () => {
      const newZoom = mapInstance.getZoom() || currentZoomLevel;
      if ((step > 0 && newZoom < targetZoom) || (step < 0 && newZoom > targetZoom)) {
        setTimeout(() => mapInstance.setZoom(newZoom + step), 80);
      } else {
        google.maps.event.removeListener(listener);
        callback?.();
      }
    });

    mapInstance.setZoom(currentZoomLevel + step);
  }, []);

  const isInViewport = useCallback((mapInstance: google.maps.Map, lat: number, lng: number): boolean => {
    const bounds = mapInstance.getBounds();
    if (!bounds) return false;
    return bounds.contains(new google.maps.LatLng(lat, lng));
  }, []);

  useImperativeHandle(ref, () => ({
    focusStore: (storeId: string, lat: number, lng: number) => {
      if (!map) return;

      if (currentInfoWindowRef.current) {
        currentInfoWindowRef.current.close();
      }

      const targetLatLng = { lat, lng };
      const currentZoomLevel = map.getZoom() || 4;
      const targetZoom = 13;

      const openInfoWindow = () => {
        setTimeout(() => {
          const markerInfo = markersRef.current.find((m) => m.store.id === storeId);
          if (markerInfo) {
            google.maps.event.trigger(markerInfo.marker, 'click');
          }
        }, 500);
      };

      const storeInView = isInViewport(map, lat, lng);

      if (currentZoomLevel >= targetZoom && storeInView) {
        map.panTo(targetLatLng);
        openInfoWindow();
      } else if (storeInView) {
        smoothZoomTo(map, targetZoom, () => {
          map.panTo(targetLatLng);
          openInfoWindow();
        });
      } else {
        const intermediateZoom = Math.max(Math.min(currentZoomLevel, targetZoom) - 3, 3);
        smoothZoomTo(map, intermediateZoom, () => {
          map.panTo(targetLatLng);
          setTimeout(() => {
            smoothZoomTo(map, targetZoom, openInfoWindow);
          }, 600);
        });
      }
    },
    getViewportNeighborhoods: () => viewportNeighborhoods,
  }), [map, smoothZoomTo, isInViewport, viewportNeighborhoods]);

  // Update markers based on zoom level (only updates styling and visibility, no recreation)
  const updateMarkersForZoom = useCallback((zoom: number) => {
    const isDetailedZoom = zoom >= 13;

    markersRef.current.forEach((markerInfo) => {
      const isCustomer = !!markerInfo.store.customer_id;
      const firstLetter = (markerInfo.layer.brands[0] || 'S').charAt(0).toUpperCase();

      if (isDetailedZoom) {
        const svgHeight = markerInfo.storeLabels.length > 0 ? 108 : 80;
        const scaledSize = getMarkerScaledSize(true, svgHeight);
        const anchorPoint = getMarkerAnchor(true, markerInfo.storeLabels.length > 0);

        markerInfo.marker.setIcon({
          url: createDetailedMarker(firstLetter, isCustomer, markerInfo.logoDataUrl, markerInfo.storeLabels),
          scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height),
          anchor: new window.google.maps.Point(anchorPoint.x, anchorPoint.y),
        });
        // Show both primary and neighborhood stores
        markerInfo.marker.setVisible(true);
      } else {
        // Show simple compact markers (zoom < 13)
        const scaledSize = getMarkerScaledSize(false);
        const anchorPoint = getMarkerAnchor(false);

        markerInfo.marker.setIcon({
          url: createSimpleMarker(isCustomer),
          scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height),
          anchor: new window.google.maps.Point(anchorPoint.x, anchorPoint.y),
        });
        // Only show primary stores when zoomed out
        markerInfo.marker.setVisible(markerInfo.isPrimaryStore);
      }
    });
  }, []);

  // Create a pulse OverlayView for a given lat/lng
  const createPulseOverlay = useCallback((latLng: google.maps.LatLng, mapInstance: google.maps.Map, isCustomer = true): google.maps.OverlayView => {
    const overlay = new google.maps.OverlayView();
    let div: HTMLDivElement | null = null;

    overlay.onAdd = function () {
      div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.borderRadius = '50%';
      div.style.pointerEvents = 'none';
      div.className = isCustomer ? 'primary-store-pulse' : 'primary-store-pulse red';
      const panes = this.getPanes();
      panes?.overlayMouseTarget?.appendChild(div);
    };

    overlay.draw = function () {
      if (!div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const zoom = mapInstance.getZoom() ?? 13;
      const zoomScale = hideClusteringRef.current ? getZoomScale(zoom) : 1;
      const userScale = markerSizeRef.current / 100;
      const size = Math.round(40 * zoomScale * userScale);
      const offset = Math.round(size / 2);
      div.style.width = `${size}px`;
      div.style.height = `${size}px`;
      const point = projection.fromLatLngToDivPixel(latLng);
      if (point) {
        div.style.left = (point.x - offset) + 'px';
        div.style.top = (point.y - offset) + 'px';
      }
    };

    overlay.onRemove = function () {
      if (div && div.parentNode) {
        div.parentNode.removeChild(div);
      }
      div = null;
    };

    overlay.setMap(mapInstance);
    return overlay;
  }, []);

  // Initialize map with useCallback to avoid dependency issues
  const initializeMapCallback = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    try {
      const newMap = new window.google.maps.Map(mapRef.current, {
        zoom: 4,
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        clickableIcons: !isTauri(), // desktop only: disable Google's built-in place popups (they open google.com/maps in a new tab — dead on desktop). Web keeps them.
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });

      // Listen for zoom changes to update markers only when crossing threshold
      zoomModeRef.current = currentZoom >= 10;
      newMap.addListener('zoom_changed', () => {
        const zoom = newMap.getZoom();
        if (zoom !== undefined) {
          setMapZoom(zoom);
          const newZoomMode = zoom >= 13;
          // Only update state and markers if we crossed the zoom level 13 threshold
          if (newZoomMode !== zoomModeRef.current) {
            setCurrentZoom(zoom);
            zoomModeRef.current = newZoomMode;
          }
        }
      });

      setMap(newMap);
      setCurrentZoom(4);
      setIsLoading(false);
      onMapReady?.(newMap);
    } catch (error) {
      console.error('Error initializing map:', error);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMarkersForZoom]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) initializeMapCallback();
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
      });
    return () => { cancelled = true; };
  }, [initializeMapCallback]);

  // Helper function to get store labels (badges) based on installed solutions
  const getStoreLabels = (store: BrandStoreForMap): string[] => {
    const labels: string[] = [];
    if (store.purchek_install_date) labels.push('P');
    if (store.containment_install_date) labels.push('CC');
    if (store.cart_manager_install_date) labels.push('CM');
    if (store.subscription_start_date) labels.push('S');
    return labels;
  };

  // Helper function to check if a store has recommendations for selected solutions
  const storeHasRecommendations = (store: BrandStoreForMap, selectedSolutions?: string[]): boolean => {
    const storeWithRecs = store as any;
    if (!storeWithRecs.recommendations) return false;

    const recs = storeWithRecs.recommendations;

    // If no specific solutions selected, check all
    if (!selectedSolutions || selectedSolutions.length === 0) {
      return (
        (recs.purchek && recs.purchek.length > 0) ||
        (recs.containment && recs.containment.length > 0) ||
        (recs.cartManager && recs.cartManager.length > 0) ||
        (recs.subscription && recs.subscription.length > 0) ||
        (recs.pairedWheel && recs.pairedWheel.length > 0)
      );
    }

    // Check only selected solutions
    return selectedSolutions.some(id => {
      if (id === 'purchek') return recs.purchek && recs.purchek.length > 0;
      if (id === 'cart-containment') return recs.containment && recs.containment.length > 0;
      if (id === 'cart-manager') return recs.cartManager && recs.cartManager.length > 0;
      if (id === 'subscription') return recs.subscription && recs.subscription.length > 0;
      if (id === 'paired-wheels') return recs.pairedWheel && recs.pairedWheel.length > 0;
      return false;
    });
  };

  // Helper function to check if a store has whitespace for selected products
  const storeHasWhitespace = (store: BrandStoreForMap, selectedProducts?: string[]): boolean => {
    // If no specific products selected, check all
    if (!selectedProducts || selectedProducts.length === 0) {
      return (
        !store.purchek_install_date ||
        !store.containment_install_date ||
        !store.cart_manager_install_date ||
        !store.subscription_start_date ||
        !store.paired_wheel_install_date
      );
    }

    // Check only selected products
    return selectedProducts.some(id => {
      if (id === 'purchek') return !store.purchek_install_date;
      if (id === 'cart-containment') return !store.containment_install_date;
      if (id === 'cart-manager') return !store.cart_manager_install_date;
      if (id === 'subscription') return !store.subscription_start_date;
      if (id === 'paired-wheels') return !store.paired_wheel_install_date;
      return false;
    });
  };

  // Debounced effect for updating markers incrementally based on viewport changes
  useEffect(() => {
    if (!map || !window.google) {
      return;
    }

    // Clear any pending debounce timer
    if (updateDebounceTimerRef.current) {
      clearTimeout(updateDebounceTimerRef.current);
    }

    updateDebounceTimerRef.current = setTimeout(async () => {
      const bounds = map.getBounds();
      const newStoresToDisplay = new Set<string>();
      const storesByLayerId: Record<string, { store: BrandStoreForMap; isPrimary: boolean; layer: LayerData }[]> = {};

      layersWithStores.forEach((layer) => {
        const primaryStores = layerStores[layer.id] || [];
        const rawNeighborhoodStores = viewportNeighborhoods[layer.id] || [];
        const neighborhoodStores = rawNeighborhoodStores.map((store: any) => {
          if (store.recommendations) return store;
          return {
            ...store,
            recommendations: calculateStoreRecommendations(
              store,
              store.estimated_no_of_carts_per_store || 0,
              recommendationConfigs.purchekConditions,
              recommendationConfigs.cartManagerConditions,
              recommendationConfigs.containmentConditions,
              recommendationConfigs.subscriptionConditions,
              recommendationConfigs.pairedWheelConditions,
              recommendationConfigs.purchekReadinessName,
              recommendationConfigs.cartManagerReadinessName,
              recommendationConfigs.containmentReadinessName,
              recommendationConfigs.subscriptionReadinessName,
              recommendationConfigs.pairedWheelReadinessName
            ),
          };
        });
        const allStores = [...primaryStores, ...neighborhoodStores];

        const filteredStores = allStores.filter((store) => {
          const isPrimary = layer.brands.includes(store.brand_name || '');

          if (layer.viewingMode === 'system-recommended') {
            return isPrimary ? storeHasRecommendations(store, layer.whitespaceOpportunities) : true;
          }
          if (layer.viewingMode === 'all-white-space') {
            return isPrimary ? storeHasWhitespace(store, layer.whitespaceOpportunities) : true;
          }
          return true;
        });

        if (!storesByLayerId[layer.id]) {
          storesByLayerId[layer.id] = [];
        }

        filteredStores.forEach((store) => {
          const storeId = `${layer.id}:${store.id}`;
          newStoresToDisplay.add(storeId);
          storesByLayerId[layer.id].push({
            store,
            isPrimary: layer.brands.includes(store.brand_name || ''),
            layer,
          });
        });
      });

      const storeIdsToRemove: string[] = [];
      markersRef.current.forEach((markerInfo) => {
        const notInCurrentData = !newStoresToDisplay.has(markerInfo.storeId);

        const isOutsideViewport = !markerInfo.isPrimaryStore && bounds &&
          markerInfo.store.latitude && markerInfo.store.longitude &&
          !bounds.contains(new window.google.maps.LatLng(markerInfo.store.latitude, markerInfo.store.longitude));

        if (notInCurrentData || isOutsideViewport) {
          markerInfo.marker.setMap(null);
          storeIdsToRemove.push(markerInfo.storeId);
        }
      });

      markersRef.current = markersRef.current.filter(
        (m) => !storeIdsToRemove.includes(m.storeId)
      );

      // Add markers for new stores that came into view
      const existingStoreIds = new Set(markersRef.current.map((m) => m.storeId));

      // Prefetch logos for all new brands in parallel
      const newBrandNames = new Set<string>();
      for (const layerId of Object.keys(storesByLayerId)) {
        for (const { store } of storesByLayerId[layerId]) {
          const storeId = `${layerId}:${store.id}`;
          if (!existingStoreIds.has(storeId) && store.brand_name) {
            newBrandNames.add(store.brand_name);
          }
        }
      }
      newBrandNames.forEach(name => fetchBrandLogo(name));

      for (const layerId of Object.keys(storesByLayerId)) {
        for (const { store, isPrimary, layer } of storesByLayerId[layerId]) {
          const storeId = `${layer.id}:${store.id}`;

          if (!existingStoreIds.has(storeId) && store.latitude && store.longitude) {
            const storeLabels = getStoreLabels(store);
            const isCustomer = !!store.customer_id;
            const firstLetter = (layer.brands[0] || 'S').charAt(0).toUpperCase();
            const isDetailedZoom = currentZoom >= 13;

            // Create marker with simple icon first (fast rendering)
            let icon;
            if (isDetailedZoom) {
              const svgHeight = storeLabels.length > 0 ? 108 : 80;
              const scaledSize = getMarkerScaledSize(true, svgHeight);
              const anchorPoint = getMarkerAnchor(true, storeLabels.length > 0);
              icon = {
                url: createDetailedMarker(firstLetter, isCustomer, '', storeLabels),
                scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height),
                anchor: new window.google.maps.Point(anchorPoint.x, anchorPoint.y),
              };
            } else {
              const scaledSize = getMarkerScaledSize(false);
              const anchorPoint = getMarkerAnchor(false);
              icon = {
                url: createSimpleMarker(isCustomer),
                scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height),
                anchor: new window.google.maps.Point(anchorPoint.x, anchorPoint.y),
              };
            }

            const marker = new window.google.maps.Marker({
              position: { lat: store.latitude, lng: store.longitude },
              map,
              title: store.site || store.brand_name || 'Store',
              icon,
              visible: isDetailedZoom || isPrimary,
            });

            const markerInfo: MarkerInfo = {
              marker,
              store,
              layer,
              logoDataUrl: '',
              storeLabels,
              isPrimaryStore: isPrimary,
              storeId,
            };

            markersRef.current.push(markerInfo);

            // Load logo in background and store in markerInfo for zoom updates
            fetchBrandLogo(store.brand_name || '').then(logoDataUrl => {
              if (logoDataUrl) {
                markerInfo.logoDataUrl = logoDataUrl;
                if (isDetailedZoom) {
                  const svgHeight = storeLabels.length > 0 ? 108 : 80;
                  const scaledSize = getMarkerScaledSize(true, svgHeight);
                  const anchorPoint = getMarkerAnchor(true, storeLabels.length > 0);
                  marker.setIcon({
                    url: createDetailedMarker(firstLetter, isCustomer, logoDataUrl, storeLabels),
                    scaledSize: new window.google.maps.Size(scaledSize.width, scaledSize.height),
                    anchor: new window.google.maps.Point(anchorPoint.x, anchorPoint.y),
                  });
                }
              }
            }).catch(() => {});

            if (isPrimary) {
              marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
            }

            marker.addListener('click', () => {
              if (currentInfoWindowRef.current) {
                currentInfoWindowRef.current.close();
              }

              const storeWithRecs = store as any;
              const recommendations = storeWithRecs.recommendations || {};

              const recommendedSolutions: string[] = [];
              if (recommendations.purchek?.length > 0) recommendedSolutions.push('Purchek');
              if (recommendations.containment?.length > 0) recommendedSolutions.push('Cart Containment');
              if (recommendations.cartManager?.length > 0) recommendedSolutions.push('Cart Manager');
              if (recommendations.subscription?.length > 0) recommendedSolutions.push('Subscription');
              if (recommendations.pairedWheel?.length > 0) recommendedSolutions.push('Paired Wheel');

              const whitespaceOpportunities: string[] = [];
              if (!store.purchek_install_date) whitespaceOpportunities.push('Purchek');
              if (!store.containment_install_date) whitespaceOpportunities.push('Cart Containment');
              if (!store.cart_manager_install_date) whitespaceOpportunities.push('Cart Manager');
              if (!store.subscription_start_date) whitespaceOpportunities.push('Subscription');
              if (!store.paired_wheel_install_date) whitespaceOpportunities.push('Paired Wheel');

              const infoContent = buildInfoWindowContent({
                ...store,
                city: (store as any).geo?.city ?? (store as any).city ?? null,
                state: (store as any).geo?.state_code ?? (store as any).state ?? null,
                crime_rate: (store as any).geo?.total_crime_incidents ?? (store as any).crime_rate ?? null,
                plaza_cm_customer_count: (store as any).nearby_penetration?.plaza_cm_customer_count ?? (store as any).plaza_cm_customer_count ?? null,
              }, {
                showStreetViewButton: true,
                recommendedSolutions,
                whitespaceOpportunities,
              });

              const infoWindow = new window.google.maps.InfoWindow({
                content: infoContent,
              });

              marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
              infoWindowJustOpenedRef.current = true;
              infoWindow.open(map, marker);
              currentInfoWindowRef.current = infoWindow;

              infoWindow.addListener('closeclick', () => {
                currentInfoWindowRef.current = null;
              });

              infoWindow.addListener('domready', () => {
                const streetViewIconBtn = document.getElementById(`street-view-icon-btn-${store.id}`);
                if (streetViewIconBtn) {
                  streetViewIconBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    setStreetViewStore({
                      lat: store.latitude!,
                      lng: store.longitude!,
                      name: store.site || store.brand_name,
                    });
                  });
                }
              });
            });
          }
        }
      }


      // Apply zoom-level styling to all current markers
      if (markersRef.current.length > 0) {
        updateMarkersForZoom(currentZoom);
      }

      // Trigger pulse overlay refresh
      setMarkerUpdateCount((c) => c + 1);
    }, 1000); // 1 second debounce

    return () => {
      if (updateDebounceTimerRef.current) {
        clearTimeout(updateDebounceTimerRef.current);
      }
    };
  }, [map, layerStores, layersWithStores, viewportNeighborhoods, fetchBrandLogo, updateMarkersForZoom, currentZoom, recommendationConfigs]);

  // Separate effect for handling zoom-level updates without recreating markers
  useEffect(() => {
    if (markersRef.current.length > 0) {
      updateMarkersForZoom(currentZoom);
    }
  }, [currentZoom, layersWithStores, updateMarkersForZoom]);

  // Dedicated effect for pulse overlays on primary stores
  useEffect(() => {
    if (!map) return;

    // Clear all existing pulse overlays
    pulseOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
    pulseOverlaysRef.current = [];

    const isDetailedZoom = currentZoom >= 13;
    if (!isDetailedZoom) return;

    // Create pulse overlays for primary stores whose layer has showNeighborhoodStores enabled
    markersRef.current.forEach((markerInfo) => {
      const currentLayer = layersWithStores.find((l) => l.id === markerInfo.layer.id);
      if (
        markerInfo.isPrimaryStore &&
        currentLayer?.showNeighborhoodStores &&
        markerInfo.store.latitude &&
        markerInfo.store.longitude
      ) {
        const latLng = new google.maps.LatLng(markerInfo.store.latitude, markerInfo.store.longitude);
        const isCustomer = !!markerInfo.store.customer_id;
        const overlay = createPulseOverlay(latLng, map, isCustomer);
        pulseOverlaysRef.current.push(overlay);
      }
    });

    return () => {
      pulseOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
      pulseOverlaysRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, currentZoom, layersWithStores, markerUpdateCount, createPulseOverlay]);

  useEffect(() => {
    if (!map || !regionViewEnabled) {
      stateMarkersRef.current.forEach(m => m.setMap(null));
      stateMarkersRef.current = [];
      return;
    }

    stateMarkersRef.current.forEach(m => m.setMap(null));
    stateMarkersRef.current = [];

    if (regionZoomTier !== 'state' || stateAggregates.length === 0) return;

    stateAggregates.forEach(({ state_code, count, customers, centroid_lat, centroid_lng }) => {
      const coords = getStateCoords(state_code);
      if (!coords) return;

      const svgUrl = createStateClusterMarker(state_code, count, customers);

      const marker = new google.maps.Marker({
        position: coords,
        map,
        icon: {
          url: svgUrl,
          scaledSize: new google.maps.Size(60, 60),
          anchor: new google.maps.Point(30, 30),
        },
        zIndex: 1,
      });

      marker.addListener('click', () => {
        // Zoom to store centroid (where stores are concentrated) instead of state center
        const focusPoint = centroid_lat && centroid_lng
          ? { lat: centroid_lat, lng: centroid_lng }
          : coords;
        map.panTo(focusPoint);
        smoothZoomTo(map, 9);
      });

      stateMarkersRef.current.push(marker);
    });

    return () => {
      stateMarkersRef.current.forEach(m => m.setMap(null));
      stateMarkersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, regionViewEnabled, regionZoomTier, stateAggregates]);

  useEffect(() => {
    const existingMap = regionMarkerMapRef.current;
    const customerMap = regionMarkerCustomerMapRef.current;

    // Full cleanup when region view is off or at state zoom
    if (!map || !regionViewEnabled || regionZoomTier === 'state') {
      if (regionClustererRef.current) {
        regionClustererRef.current.clearMarkers();
        regionClustererRef.current = null;
      }
      existingMap.forEach(m => m.setMap(null));
      existingMap.clear();
      prevRegionZoomTierRef.current = '';
      return;
    }

    const isDetail = regionZoomTier === 'detail';
    // Skip clustering when hideClustering is enabled
    const skipClustering = hideClustering;

    // If zoom tier changed (cluster <-> detail) or clustering mode changed, full rebuild needed
    const tierChanged = prevRegionZoomTierRef.current !== regionZoomTier;
    const clusteringModeChanged = prevSkipClusteringRef.current !== skipClustering;
    prevRegionZoomTierRef.current = regionZoomTier;
    prevSkipClusteringRef.current = skipClustering;

    if (tierChanged || clusteringModeChanged) {
      if (regionClustererRef.current) {
        regionClustererRef.current.clearMarkers();
        regionClustererRef.current = null;
      }
      existingMap.forEach(m => m.setMap(null));
      existingMap.clear();
    }

    if (regionViewStores.length === 0 && existingMap.size === 0) return;

    regionViewCancelledRef.current = false;

    // Build set of incoming store IDs
    const incomingIds = new Set(regionViewStores.map(s => s.id));

    // Remove markers for stores no longer in viewport
    const removedMarkers: google.maps.Marker[] = [];
    existingMap.forEach((marker, id) => {
      if (!incomingIds.has(id)) {
        marker.setMap(null);
        existingMap.delete(id);
        removedMarkers.push(marker);
      }
    });

    // Remove departed markers from clusterer
    if (regionClustererRef.current && removedMarkers.length > 0) {
      regionClustererRef.current.removeMarkers(removedMarkers);
    }

    // Find stores that need new markers
    const newStores = regionViewStores.filter(s => !existingMap.has(s.id) && s.latitude && s.longitude);

    if (newStores.length === 0 && removedMarkers.length === 0) return;

    const buildNewMarkers = async () => {
      const addedMarkers: google.maps.Marker[] = [];

      for (const store of newStores) {
        if (regionViewCancelledRef.current) return;

        const isCustomer = !!store.customer_id;
        const firstLetter = (store.brand_name || 'S').charAt(0).toUpperCase();
        const storeLabels: string[] = [];
        if (store.purchek_install_date) storeLabels.push('P');
        if (store.containment_install_date) storeLabels.push('CC');
        if (store.cart_manager_install_date) storeLabels.push('CM');
        if (store.subscription_start_date) storeLabels.push('S');

        const userScale = markerSizeRef.current / 100;
        const baseH = storeLabels.length > 0 ? 108 : 80;
        let icon;
        if (isDetail) {
          const scaledSize = getMarkerScaledSize(true, baseH);
          const anchorPoint = getMarkerAnchor(true, storeLabels.length > 0);
          icon = {
            url: createDetailedMarker(firstLetter, isCustomer, '', storeLabels),
            scaledSize: new google.maps.Size(Math.round(scaledSize.width * userScale), Math.round(scaledSize.height * userScale)),
            anchor: new google.maps.Point(Math.round(anchorPoint.x * userScale), Math.round(anchorPoint.y * userScale)),
          };
        } else {
          const scaledSize = getMarkerScaledSize(false);
          const anchorPoint = getMarkerAnchor(false);
          icon = {
            url: createSimpleMarker(isCustomer),
            scaledSize: new google.maps.Size(scaledSize.width, scaledSize.height),
            anchor: new google.maps.Point(anchorPoint.x, anchorPoint.y),
          };
        }

        const marker = new google.maps.Marker({
          position: { lat: store.latitude, lng: store.longitude },
          title: store.site || store.brand_name || 'Store',
          icon,
          zIndex: 10,
        });

        if (isDetail) {
          fetchBrandLogo(store.brand_name || '').then(logoDataUrl => {
            if (logoDataUrl && !regionViewCancelledRef.current) {
              const s = markerSizeRef.current / 100;
              const svgHeight = storeLabels.length > 0 ? 108 : 80;
              const scaledSize = getMarkerScaledSize(true, svgHeight);
              const anchorPoint = getMarkerAnchor(true, storeLabels.length > 0);
              marker.setIcon({
                url: createDetailedMarker(firstLetter, isCustomer, logoDataUrl, storeLabels),
                scaledSize: new google.maps.Size(Math.round(scaledSize.width * s), Math.round(scaledSize.height * s)),
                anchor: new google.maps.Point(Math.round(anchorPoint.x * s), Math.round(anchorPoint.y * s)),
              });
            }
          }).catch(() => {});
        }

        marker.addListener('click', () => {
          if (currentInfoWindowRef.current) currentInfoWindowRef.current.close();

          const recommendations = calculateStoreRecommendations(
            store,
            (store as any).estimated_no_of_carts_per_store || 0,
            recommendationConfigs.purchekConditions,
            recommendationConfigs.cartManagerConditions,
            recommendationConfigs.containmentConditions,
            recommendationConfigs.subscriptionConditions,
            recommendationConfigs.pairedWheelConditions,
            recommendationConfigs.purchekReadinessName,
            recommendationConfigs.cartManagerReadinessName,
            recommendationConfigs.containmentReadinessName,
            recommendationConfigs.subscriptionReadinessName,
            recommendationConfigs.pairedWheelReadinessName,
          );

          const recommendedSolutions: string[] = [];
          if (recommendations.purchek?.length > 0) recommendedSolutions.push('Purchek');
          if (recommendations.containment?.length > 0) recommendedSolutions.push('Cart Containment');
          if (recommendations.cartManager?.length > 0) recommendedSolutions.push('Cart Manager');
          if (recommendations.subscription?.length > 0) recommendedSolutions.push('Subscription');
          if (recommendations.pairedWheel?.length > 0) recommendedSolutions.push('Paired Wheel');

          const whitespaceOpportunities: string[] = [];
          if (!store.purchek_install_date) whitespaceOpportunities.push('Purchek');
          if (!store.containment_install_date) whitespaceOpportunities.push('Cart Containment');
          if (!store.cart_manager_install_date) whitespaceOpportunities.push('Cart Manager');
          if (!store.subscription_start_date) whitespaceOpportunities.push('Subscription');
          if (!store.paired_wheel_install_date) whitespaceOpportunities.push('Paired Wheel');

          const infoContent = buildInfoWindowContent({
            ...store,
            city: store.geo?.city ?? null,
            state: store.geo?.state_code ?? null,
            crime_rate: store.geo?.total_crime_incidents ?? null,
            plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
          }, {
            showStreetViewButton: true,
            recommendedSolutions,
            whitespaceOpportunities,
          });

          const infoWindow = new google.maps.InfoWindow({ content: infoContent });
          infoWindowJustOpenedRef.current = true;
          infoWindow.open(map, marker);
          currentInfoWindowRef.current = infoWindow;

          infoWindow.addListener('closeclick', () => {
            currentInfoWindowRef.current = null;
          });

          infoWindow.addListener('domready', () => {
            const btn = document.getElementById(`street-view-icon-btn-${store.id}`);
            btn?.addEventListener('click', (e) => {
              e.stopPropagation();
              setStreetViewStore({ lat: store.latitude!, lng: store.longitude!, name: store.site || store.brand_name });
            });
          });
        });

        customerMap.set(marker, isCustomer);
        if (isDetail) {
          markerBaseDimsRef.current.set(marker, {
            w: 80,
            h: storeLabels.length > 0 ? 108 : 80,
            ax: 40,
            ay: storeLabels.length > 0 ? 50 : 40,
          });
        }
        existingMap.set(store.id, marker);
        addedMarkers.push(marker);
      }

      if (regionViewCancelledRef.current) return;

      // Add new markers to clusterer or map
      if (!isDetail && !skipClustering) {
        if (regionClustererRef.current) {
          // Incrementally add new markers to existing clusterer
          if (addedMarkers.length > 0) {
            regionClustererRef.current.addMarkers(addedMarkers);
          }
        } else {
          // First time: create clusterer with all current markers
          const allMarkers = Array.from(existingMap.values());
          regionClustererRef.current = new MarkerClusterer({
            map,
            markers: allMarkers,
            algorithm: new SuperClusterAlgorithm({ minPoints: 50 }),
            renderer: {
              render: ({ count, position, markers }) => {
                const custCount = markers.filter(m => customerMap.get(m as google.maps.Marker)).length;
                return new google.maps.Marker({
                  position,
                  icon: {
                    url: createRegionClusterIcon(count, custCount > count / 2),
                    scaledSize: new google.maps.Size(48, 48),
                    anchor: new google.maps.Point(24, 24),
                  },
                  zIndex: count,
                });
              },
            },
          });
        }
      } else {
        // Detail mode or clustering hidden — clear any existing clusterer and show markers directly
        if (regionClustererRef.current) {
          regionClustererRef.current.clearMarkers();
          regionClustererRef.current = null;
          // Re-add all existing markers directly to the map
          existingMap.forEach(m => m.setMap(map));
        }
        addedMarkers.forEach(m => m.setMap(map));
      }
    };

    buildNewMarkers();

    return () => {
      regionViewCancelledRef.current = true;
      // Note: markers are NOT removed here — they persist across re-renders.
      // Full cleanup happens when guard conditions are met (state zoom, disabled).
    };
  }, [map, regionViewEnabled, regionZoomTier, regionViewStores, hideClustering, fetchBrandLogo, recommendationConfigs.purchekConditions, recommendationConfigs.cartManagerConditions, recommendationConfigs.containmentConditions, recommendationConfigs.subscriptionConditions, recommendationConfigs.pairedWheelConditions, recommendationConfigs.purchekReadinessName, recommendationConfigs.cartManagerReadinessName, recommendationConfigs.containmentReadinessName, recommendationConfigs.subscriptionReadinessName, recommendationConfigs.pairedWheelReadinessName]);

  // Region view: Neighbourhood stores fetch + render when showNeighbourhood enabled & zoom >= 13
  useEffect(() => {
    const nMap = neighbourhoodMarkerMapRef.current;

    if (!map || !regionViewEnabled || !showNeighbourhood) {
      // Cleanup neighbourhood markers when disabled
      nMap.forEach(m => m.setMap(null));
      nMap.clear();
      neighbourhoodBoundsKeyRef.current = '';
      // Cleanup region pulse overlays
      regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
      regionPulseOverlaysRef.current = [];
      return;
    }

    let cancelled = false;

    const handleIdle = async () => {
      if (cancelled || !map) return;

      // Skip re-fetch if an info window just opened (auto-pan triggers idle)
      if (infoWindowJustOpenedRef.current) {
        infoWindowJustOpenedRef.current = false;
        return;
      }

      const zoom = map.getZoom() ?? 4;

      if (zoom < 13) {
        // Not detailed enough, clear neighbourhood
        nMap.forEach(m => m.setMap(null));
        nMap.clear();
        neighbourhoodBoundsKeyRef.current = '';
        regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
        regionPulseOverlaysRef.current = [];
        return;
      }

      const bounds = map.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const boundsKey = `${Math.round(sw.lat() * 100)},${Math.round(sw.lng() * 100)},${Math.round(ne.lat() * 100)},${Math.round(ne.lng() * 100)}|${neighbourhoodRetailers.join(',')}|${neighbourhoodChannels.join(',')}|${neighbourhoodStatus}|${neighbourhoodSolutions.join(',')}|${neighbourhoodWhitespace.join(',')}`;

      if (boundsKey === neighbourhoodBoundsKeyRef.current) return;
      neighbourhoodBoundsKeyRef.current = boundsKey;

      // Find primary stores in viewport
      const primaryInView = regionViewStores.filter(s =>
        s.latitude && s.longitude && bounds.contains(new google.maps.LatLng(s.latitude, s.longitude))
      );

      if (primaryInView.length === 0) {
        nMap.forEach(m => m.setMap(null));
        nMap.clear();
        regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
        regionPulseOverlaysRef.current = [];
        return;
      }

      try {
        const neighbourStores = await fetchNeighborhoodStoresForViewport(
          primaryInView,
          3,
          neighbourhoodStatus,
          neighbourhoodChannels.length > 0 ? neighbourhoodChannels : undefined,
          neighbourhoodRetailers.length > 0 ? neighbourhoodRetailers : undefined,
          neighbourhoodSolutions.length > 0 ? neighbourhoodSolutions : undefined,
          neighbourhoodWhitespace.length > 0 ? neighbourhoodWhitespace : undefined
        );
        if (cancelled) return;

        // Exclude stores already shown as region markers
        const regionStoreIds = new Set(regionViewStores.map(s => s.id));

        // Build set of incoming neighbourhood store IDs
        const incomingIds = new Set<string>();
        neighbourStores.forEach(store => {
          if (store.latitude && store.longitude && !regionStoreIds.has(store.id)) {
            incomingIds.add(store.id);
          }
        });

        // Remove markers no longer in the new data
        nMap.forEach((marker, id) => {
          if (!incomingIds.has(id)) {
            marker.setMap(null);
            nMap.delete(id);
          }
        });

        // Add markers only for new stores not already on the map
        neighbourStores.forEach(store => {
          if (!store.latitude || !store.longitude || regionStoreIds.has(store.id)) return;
          if (nMap.has(store.id)) return; // already displayed

          const isCustomer = !!store.customer_id;
          const firstLetter = neighbourhoodAnonymous ? 'S' : (store.brand_name || 'S').charAt(0).toUpperCase();
          const anonIcon = neighbourhoodAnonymous ? getAnonymousStoreIcon() : '';
          const storeLabels: string[] = [];
          if (store.purchek_install_date) storeLabels.push('P');
          if (store.containment_install_date) storeLabels.push('CC');
          if (store.cart_manager_install_date) storeLabels.push('CM');
          if (store.subscription_start_date) storeLabels.push('S');

          const nScale = markerSizeRef.current / 100;
          const svgHeight = storeLabels.length > 0 ? 108 : 80;
          const scaledSize = getMarkerScaledSize(true, svgHeight);
          const anchorPoint = getMarkerAnchor(true, storeLabels.length > 0);

          const marker = new google.maps.Marker({
            position: { lat: store.latitude, lng: store.longitude },
            map,
            title: neighbourhoodAnonymous ? 'Neighbourhood Store' : (store.site || store.brand_name || 'Neighbourhood Store'),
            icon: {
              url: createDetailedMarker(firstLetter, isCustomer, anonIcon, storeLabels),
              scaledSize: new google.maps.Size(Math.round(scaledSize.width * nScale), Math.round(scaledSize.height * nScale)),
              anchor: new google.maps.Point(Math.round(anchorPoint.x * nScale), Math.round(anchorPoint.y * nScale)),
            },
            zIndex: 1,
          });

          markerBaseDimsRef.current.set(marker, {
            w: 80,
            h: svgHeight,
            ax: 40,
            ay: storeLabels.length > 0 ? 50 : 40,
          });

          // Load logo in background (skip when anonymous)
          if (!neighbourhoodAnonymous) {
            fetchBrandLogo(store.brand_name || '').then(logoDataUrl => {
              if (logoDataUrl && !cancelled) {
                const s = markerSizeRef.current / 100;
                marker.setIcon({
                  url: createDetailedMarker(firstLetter, isCustomer, logoDataUrl, storeLabels),
                  scaledSize: new google.maps.Size(Math.round(scaledSize.width * s), Math.round(scaledSize.height * s)),
                  anchor: new google.maps.Point(Math.round(anchorPoint.x * s), Math.round(anchorPoint.y * s)),
                });
              }
            }).catch(() => {});
          }

          // Info window for neighbourhood markers
          marker.addListener('click', () => {
            if (currentInfoWindowRef.current) currentInfoWindowRef.current.close();

            const recommendations = calculateStoreRecommendations(
              store,
              (store as any).estimated_no_of_carts_per_store || 0,
              recommendationConfigs.purchekConditions,
              recommendationConfigs.cartManagerConditions,
              recommendationConfigs.containmentConditions,
              recommendationConfigs.subscriptionConditions,
              recommendationConfigs.pairedWheelConditions,
              recommendationConfigs.purchekReadinessName,
              recommendationConfigs.cartManagerReadinessName,
              recommendationConfigs.containmentReadinessName,
              recommendationConfigs.subscriptionReadinessName,
              recommendationConfigs.pairedWheelReadinessName,
            );

            const recommendedSolutions: string[] = [];
            if (recommendations.purchek?.length > 0) recommendedSolutions.push('Purchek');
            if (recommendations.containment?.length > 0) recommendedSolutions.push('Cart Containment');
            if (recommendations.cartManager?.length > 0) recommendedSolutions.push('Cart Manager');
            if (recommendations.subscription?.length > 0) recommendedSolutions.push('Subscription');
            if (recommendations.pairedWheel?.length > 0) recommendedSolutions.push('Paired Wheel');

            const whitespaceOpportunities: string[] = [];
            if (!store.purchek_install_date) whitespaceOpportunities.push('Purchek');
            if (!store.containment_install_date) whitespaceOpportunities.push('Cart Containment');
            if (!store.cart_manager_install_date) whitespaceOpportunities.push('Cart Manager');
            if (!store.subscription_start_date) whitespaceOpportunities.push('Subscription');
            if (!store.paired_wheel_install_date) whitespaceOpportunities.push('Paired Wheel');

            const infoContent = buildInfoWindowContent({
              ...store,
              city: store.geo?.city ?? null,
              state: store.geo?.state_code ?? null,
              crime_rate: store.geo?.total_crime_incidents ?? null,
              plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
            }, {
              showStreetViewButton: true,
              showNeighbourhoodButton: false,
              recommendedSolutions,
              whitespaceOpportunities,
            });

            const infoWindow = new google.maps.InfoWindow({ content: infoContent });
            infoWindowJustOpenedRef.current = true;
            infoWindow.open(map, marker);
            currentInfoWindowRef.current = infoWindow;

            infoWindow.addListener('closeclick', () => {
              currentInfoWindowRef.current = null;
            });

            infoWindow.addListener('domready', () => {
              const btn = document.getElementById(`street-view-icon-btn-${store.id}`);
              btn?.addEventListener('click', (e) => {
                e.stopPropagation();
                setStreetViewStore({ lat: store.latitude!, lng: store.longitude!, name: store.site || store.brand_name });
              });
            });
          });

          nMap.set(store.id, marker);
        });

        // Refresh pulse overlays on primary region stores in viewport
        regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
        regionPulseOverlaysRef.current = [];
        primaryInView.forEach(store => {
          if (store.latitude && store.longitude) {
            const latLng = new google.maps.LatLng(store.latitude, store.longitude);
            const isCustomer = !!store.customer_id;
            const overlay = createPulseOverlay(latLng, map, isCustomer);
            regionPulseOverlaysRef.current.push(overlay);
          }
        });
      } catch (err) {
        console.error('Error fetching neighbourhood stores:', err);
      }
    };

    const idleListener = map.addListener('idle', handleIdle);
    handleIdle();

    return () => {
      cancelled = true;
      idleListener.remove();
      nMap.forEach(m => m.setMap(null));
      nMap.clear();
      neighbourhoodBoundsKeyRef.current = '';
      regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
      regionPulseOverlaysRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, regionViewEnabled, showNeighbourhood, neighbourhoodRetailers, neighbourhoodChannels, neighbourhoodStatus, neighbourhoodSolutions, neighbourhoodWhitespace, neighbourhoodAnonymous, regionViewStores, fetchBrandLogo, createPulseOverlay, recommendationConfigs]);

  // Dynamic scaling for all markers based on zoom (when clustering hidden) and user marker size
  useEffect(() => {
    if (!map) return;

    const zoomScale = hideClustering ? getZoomScale(mapZoom) : 1;
    const userScale = markerSize / 100;
    const scale = zoomScale * userScale;

    const scaleMarker = (marker: google.maps.Marker) => {
      const icon = marker.getIcon() as google.maps.Icon | undefined;
      if (!icon?.url) return;
      const dims = markerBaseDimsRef.current.get(marker);
      if (!dims) return; // simple dot markers have no dims entry — skip them
      marker.setIcon({
        ...icon,
        scaledSize: new google.maps.Size(Math.round(dims.w * scale), Math.round(dims.h * scale)),
        anchor: new google.maps.Point(Math.round(dims.ax * scale), Math.round(dims.ay * scale)),
      });
    };

    regionMarkerMapRef.current.forEach(scaleMarker);
    neighbourhoodMarkerMapRef.current.forEach(scaleMarker);

    // Force pulse overlays to redraw with new size
    [...regionPulseOverlaysRef.current, ...pulseOverlaysRef.current].forEach(o => o.draw());
  }, [map, mapZoom, hideClustering, markerSize]);

  // Full cleanup on unmount
  useEffect(() => {
    const markerMap = regionMarkerMapRef.current;
    const nMap = neighbourhoodMarkerMapRef.current;
    return () => {
      if (regionClustererRef.current) {
        regionClustererRef.current.clearMarkers();
        regionClustererRef.current = null;
      }
      markerMap.forEach(m => m.setMap(null));
      markerMap.clear();
      nMap.forEach(m => m.setMap(null));
      nMap.clear();
      regionPulseOverlaysRef.current.forEach(o => o.setMap(null));
      regionPulseOverlaysRef.current = [];
    };
  }, []);

  return (
    <div className="opportunity-map-container">

      {/* Map Area */}
      <div className="map-area">
        {isLoading && (
          <div className="map-loading">
            <div className="spinner"></div>
            <p>Loading map...</p>
          </div>
        )}

        <div ref={mapRef} className="google-map" style={{ display: isLoading ? 'none' : 'block' }} />
        <div className="map-legend">
          <span className="map-legend-item"><span className="map-legend-dot blue"></span>Customer</span>
          <span className="map-legend-item"><span className="map-legend-dot red"></span>Non-Customer</span>
          <span className="map-legend-divider"></span>
          <span className="map-legend-item"><span className="map-legend-badge">P</span>Purchek</span>
          <span className="map-legend-item"><span className="map-legend-badge">CC</span>Cart Containment</span>
          <span className="map-legend-item"><span className="map-legend-badge">CM</span>Cart Manager</span>
          <span className="map-legend-item"><span className="map-legend-badge">S</span>Subscription</span>
        </div>

        {!hasBrandLayers && !regionViewEnabled && (
          <div className="map-empty-state">
            <div className="empty-state-content">
              <div className="empty-state-icon">
                <CgArrowsExpandLeft size={56} color="rgb(234, 88, 12)" />
              </div>
              <h3 className="empty-state-title">Add Your First Brand Layer</h3>
              <p className="empty-state-description">
                Start by adding a brand layer from the left panel to see sites on the map
              </p>
              <p className="empty-state-link">
                Ready to build your opportunity map
              </p>
            </div>
          </div>
        )}

        {regionViewEnabled && !isLoading && !regionLoading && (
          (regionZoomTier === 'state' && stateAggregates.length === 0) ||
          (regionZoomTier !== 'state' && regionViewStores.length === 0)
        ) && (
          <div className="map-no-results-overlay">
            <p>No opportunities available for the applied filters</p>
          </div>
        )}
      </div>

      {/* Street View Panel */}
      {streetViewStore && (
        <StreetViewPanel
          lat={streetViewStore.lat}
          lng={streetViewStore.lng}
          storeName={streetViewStore.name}
          onClose={() => setStreetViewStore(null)}
        />
      )}
    </div>
  );
});

export default OpportunityMap;
